import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';

interface ArticleContentProps {
  content: string;
  isPremium: boolean;
  hasPurchased?: boolean;
}

export function ArticleContent({
  content,
  isPremium,
  hasPurchased = false,
}: ArticleContentProps) {
  const { slug, locale } = useParams<{ slug: string; locale: string }>();
  const { t, i18n } = useTranslation('blog');
  const currentLocale = locale || i18n.language || 'fr';

  useEffect(() => {
    // Add IDs to headings for TOC navigation
    const headingElements = document.querySelectorAll(
      '.article-content h2, .article-content h3'
    );

    headingElements.forEach((heading, index) => {
      const text = heading.textContent || '';
      const id = text
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9\s]/g, '')
        .replace(/\s+/g, '-');

      heading.id = `heading-${index}-${id}`;
    });
  }, [content]);

  // Premium paywall: show only first 1000 words
  const shouldShowPaywall = isPremium && !hasPurchased;
  let displayContent = content;

  if (shouldShowPaywall) {
    // Split by spaces while preserving newlines
    const words = content.split(/(\s+)/); // Keep delimiters (spaces and newlines)
    let wordCount = 0;
    let cutIndex = 0;

    for (let i = 0; i < words.length; i++) {
      if (!/^\s+$/.test(words[i])) { // If not whitespace
        wordCount++;
        if (wordCount >= 1000) {
          cutIndex = i;
          break;
        }
      }
    }

    if (wordCount >= 1000) {
      displayContent = words.slice(0, cutIndex).join('') + '...';
    }
  }

  return (
    <div className="relative">
      <div className="article-content prose prose-lg max-w-none text-justify
                   prose-headings:font-heading prose-headings:text-gray-900
                   prose-h2:text-3xl prose-h2:font-bold prose-h2:mt-16 prose-h2:mb-6 prose-h2:text-left
                   prose-h3:text-2xl prose-h3:font-semibold prose-h3:mt-12 prose-h3:mb-4 prose-h3:text-left
                   prose-p:text-base prose-p:leading-loose prose-p:text-gray-800 prose-p:mb-6
                   prose-a:text-aspexit-green prose-a:no-underline hover:prose-a:underline prose-a:font-medium
                   prose-strong:text-gray-900 prose-strong:font-semibold
                   prose-ul:my-6 prose-ul:space-y-2 prose-ol:my-6 prose-ol:space-y-2
                   prose-li:text-gray-700 prose-li:leading-relaxed prose-li:text-base
                   prose-img:rounded-lg prose-img:my-8 prose-img:shadow-md
                   prose-blockquote:border-l-4 prose-blockquote:border-aspexit-green
                   prose-blockquote:pl-6 prose-blockquote:py-2 prose-blockquote:italic prose-blockquote:text-gray-600
                   prose-code:text-sm prose-code:bg-gray-100 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded
                   prose-pre:bg-gray-900 prose-pre:text-gray-100 prose-pre:p-4 prose-pre:rounded-lg prose-pre:overflow-x-auto
                   prose-table:border-collapse prose-table:w-full prose-table:my-8
                   prose-th:border prose-th:border-gray-300 prose-th:bg-gray-50 prose-th:p-3
                   prose-td:border prose-td:border-gray-300 prose-td:p-3">
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          rehypePlugins={[rehypeRaw, rehypeSanitize]}
          components={{
            h2: ({ node, ...props }) => <h2 className="scroll-mt-24" {...props} />,
            h3: ({ node, ...props }) => <h3 className="scroll-mt-24" {...props} />,
            a: ({ node, href, children, ...props }) => {
              // Embed YouTube links as iframes
              if (href && href.includes('youtube.com/embed/')) {
                return (
                  <div className="my-8 aspect-video w-full overflow-hidden rounded-lg shadow-md">
                    <iframe
                      src={href}
                      className="w-full h-full"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      title={typeof children === 'string' ? children : 'Vidéo YouTube'}
                    />
                  </div>
                );
              }
              return <a href={href} target="_blank" rel="noopener noreferrer" {...props}>{children}</a>;
            },
          }}
        >
          {displayContent}
        </ReactMarkdown>
      </div>

      {/* Premium Paywall */}
      {shouldShowPaywall && (
        <div className="mt-12 p-8 bg-gradient-to-b from-white to-aspexit-gray-light border border-aspexit-purple rounded-lg text-center">
          <div className="max-w-md mx-auto">
            <div className="w-16 h-16 bg-aspexit-purple text-white rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-8 h-8"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                />
              </svg>
            </div>
            <h3 className="text-2xl font-heading font-bold text-aspexit-black mb-2">
              {t('premiumPaywall.title')}
            </h3>
            <p className="text-aspexit-gray mb-6">
              {t('premiumPaywall.description')}
            </p>
            <div className="flex justify-center">
              <Link
                to={`/${currentLocale}/checkout/article/${slug}`}
                className="px-6 py-3 bg-aspexit-green text-white rounded-md font-medium hover:bg-aspexit-green-dark transition-colors text-center"
              >
                {t('premiumPaywall.buyButton')}
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
